import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isAuthEnabled } from '@/env';

interface Props {
  children: ReactNode;
}

/**
 * Route boundary. When auth is disabled (local dev), pass through. When
 * enabled and the user isn't authenticated, redirect to `/` with the
 * intended path stashed in router state so LoginPage can return them
 * after sign-in.
 */
export function RequireAuth({ children }: Props) {
  const isAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthEnabled()) return;
    if (inProgress !== 'none') return;
    if (!isAuthed) {
      // T-17 (T17-D13) — carry the FULL intended location (path + query + hash),
      // not just the pathname, so a deep link survives sign-in. LoginPage
      // sanitises it before use.
      navigate('/', {
        replace: true,
        state: { from: location.pathname + location.search + location.hash },
      });
    }
  }, [isAuthed, inProgress, navigate, location.pathname, location.search, location.hash]);

  if (!isAuthEnabled()) return <>{children}</>;
  if (inProgress !== 'none') return <div className="auth-loading">Signing in…</div>;
  if (!isAuthed) return null; // effect above redirects; nothing to render meanwhile

  return <>{children}</>;
}
